import React, { useState, useRef, useCallback, useEffect } from "react";
import { SERVER_URL, TRANSPORTER_NAME } from "../config/config.js";
import "./MISReport.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import { apiFetch } from "../utils/apiUtils.js";
import Autosuggest from "react-autosuggest";
import EditableInput from "./EditableInput.js";
import ReportExcel from "./ReportExcel.js";
import DraggableReportTable from "./DraggableReportTable.js";
import Popup from "reactjs-popup";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";

function MISReport({ sessionObject }) {
  const branchId = sessionObject.sessionVariables.branch_id;
  const roleId = Number(sessionObject.sessionVariables.role_id);
  const [excelData, setExcelData] = useState([]);

  const [data, setData] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [dateState, setDateState] = useState({
    date_from: new Date(),
    date_to: new Date(),
  });
  const [consignorSuggestions, setConsignorSuggestions] = useState([]);
  const [consignorInput, setConsignorInput] = useState("");
  const [selectedConsignorId, setSelectedConsignorId] = useState(null);
  const [finalInput, setFinalInput] = useState({});
  const download_ref = useRef(null);
  const abortControllerRef = useRef(null);

  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [savedMisColumns, setSavedMisColumns] = useState([]);

  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({
    email_from: "",
    email: "",
    cc_emails: "",
    subject: "",
    body_start: "",
    body_end: "",
  });
  const [emailStatus, setEmailStatus] = useState({
    show: false,
    type: "",
    message: "",
  });

  let variablesFromSession = {
    role_id: String(sessionObject.sessionVariables.role_id),
  };

  function formatDateIfValid(dateString) {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d)) return dateString;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  /** ---------------- Columns as State so we can reorder ---------------- */
  const allColumns = [
    {
      Header: "Bilty No",
      accessor: "bilty_no",
      canFilter: true,
      disableDrag: true,
    },
    { Header: "Bilty Date", accessor: "bilty_date", canFilter: true },
    { Header: "Station From", accessor: "station_from_name", canFilter: true },
    {
      Header: "Consignor Name",
      accessor: "consignor_name",
      canFilter: true,
      minWidth: "190px",
    },
    {
      Header: "Consignee Name",
      accessor: "consignee_name",
      canFilter: true,
      minWidth: "190px",
    },
    {
      Header: "Automation Status",
      accessor: "automation_status",
      canFilter: true,
      minWidth: "190px",
    },
    { Header: "Invoice No", accessor: "private_marka_no", canFilter: true },
    { Header: "Station To", accessor: "station_to_name", canFilter: true },
    { Header: "Challan Date", accessor: "crossing_date", canFilter: true },
    { Header: "Packages", accessor: "pkgs", canFilter: true },
    { Header: "Rate", accessor: "rate", canFilter: true },
    { Header: "Weight", accessor: "weight", canFilter: true },
    { Header: "Bilty Charge", accessor: "bilty_charge", canFilter: true },
    { Header: "Hamali Charge", accessor: "hamali", canFilter: true },
    { Header: "DD Charge", accessor: "door_del_charge", canFilter: true },
    { Header: "Total Amount", accessor: "total_amount", canFilter: true },
    { Header: "Other Amount", accessor: "other_amount", canFilter: true },
    { Header: "MR No", accessor: "mr_no", canFilter: true },
    {
      Header: "Delivery Date/Status",
      accessor: "delivery_status",
      canFilter: true,
      Cell: ({ row, value }) => (
        <EditableInput
          mode={
            /^\d{2}[-/]\d{2}[-/]\d{4}$/.test(value || "") ? "date" : "status"
          }
          value={value}
          placeholder="Enter delivery status or pick date"
          onSave={async (newVal, mode) => {
            try {
              const biltyId = row.original.bilty_id;

              const existingDateFormatted = formatDateIfValid(
                row.original.delivery_status
              );
              const isExistingDate =
                /^\d{2}-\d{2}-\d{4}$/.test(existingDateFormatted);

              const manuallDate =
                mode === "date"
                  ? newVal
                  : isExistingDate
                  ? existingDateFormatted
                  : "";

              const biltyRemark =
                mode === "status"
                  ? newVal
                  : row.original.bilty_remark || "";

              const endpoint = `${SERVER_URL}/bilty/update_delevery_date_maunally?bilty_id=${biltyId}&manuall_date=${encodeURIComponent(
                manuallDate
              )}&bilty_remark=${encodeURIComponent(biltyRemark)}`;

              const response = await apiFetch(
                endpoint,
                {
                  method: "POST",
                  headers: { accept: "application/json" },
                },
                sessionObject
              );

              if (!response.ok) {
                throw new Error(
                  `HTTP ${response.status} - ${response.statusText}`
                );
              }

              setData((prev) =>
                prev.map((r) =>
                  r.bilty_id === biltyId
                    ? {
                        ...r,
                        delivery_status: newVal,
                        bilty_remark:
                          mode === "status"
                            ? newVal
                            : r.bilty_remark,
                      }
                    : r
                )
              );

              toast.success(
                mode === "date"
                  ? "Delivery date updated successfully!"
                  : "Status updated successfully!"
              );
            } catch (err) {
              console.error("Save failed:", err);
              toast.error("Unable to update record.");
            }
          }}
        />
      ),
    },
    {
      Header: "Remarks",
      accessor: "bilty_remark",
      canFilter: true,
      renderExcel: (row) => {
        const hasCrossing = row.crossing_date && row.remarks;
        return hasCrossing ? row.remarks : (row.bilty_remark || "");
      },
      Cell: ({ row, value }) => {
        const hasCrossing = row.original.crossing_date && row.original.remarks;
        const displayValue = hasCrossing ? row.original.remarks : (value || "");
        return (
          <EditableInput
            mode="text"
            value={displayValue}
            readOnly={hasCrossing}
            placeholder="Type here to add remarks..."
            onSave={async (newVal) => {
              if (hasCrossing) return;
              const oldVal = row.original.bilty_remark || "";

              if (!newVal?.trim() || newVal.trim() === oldVal.trim()) {
                return;
              }

              try {
                const biltyId = row.original.bilty_id;
                const rawDate = row.original.delivery_status;

                let manuallDate = "";

                if (/^\d{2}-\d{2}-\d{4}$/.test(rawDate)) {
                  manuallDate = rawDate;
                }
                else if (rawDate) {
                  const parsed = new Date(rawDate);
                  if (!isNaN(parsed)) {
                    const day = String(parsed.getDate()).padStart(2, "0");
                    const month = String(parsed.getMonth() + 1).padStart(2, "0");
                    const year = parsed.getFullYear();
                    manuallDate = `${day}-${month}-${year}`;
                  }
                }

                const response = await apiFetch(
                  `${SERVER_URL}/bilty/update_delevery_date_maunally?bilty_id=${biltyId}&manuall_date=${encodeURIComponent(
                    manuallDate
                  )}&bilty_remark=${encodeURIComponent(newVal)}`,
                  {
                    method: "POST",
                    headers: { accept: "application/json" },
                  },
                  sessionObject
                );

                if (!response.ok) {
                  throw new Error(`HTTP ${response.status}`);
                }

                setData((prev) =>
                  prev.map((r) =>
                    r.bilty_id === biltyId
                      ? { ...r, bilty_remark: newVal }
                      : r
                  )
                );

                toast.success("Remarks updated successfully!");
              } catch (err) {
                console.error("Failed to update remarks:", err);
                toast.error("Failed to update remarks.");
              }
            }}
          />
        );
      },
    },
  ];

  const restrictedCols = [
    "rate",
    "bilty_charge",
    "hamali",
    "door_del_charge",
    "total_amount",
    "other_amount",
  ];

  const [columns, setColumns] = useState(() => {
    if (roleId === 1 || roleId === 2) {
      return allColumns;
    }
    return allColumns.filter((c) => !restrictedCols.includes(c.accessor));
  });

  const visibleColumns = React.useMemo(() => {
    if (!savedMisColumns || savedMisColumns.length === 0) return columns;
    return columns.filter((c) => savedMisColumns.includes(c.accessor));
  }, [columns, savedMisColumns]);

  const handleColumnReorder = (newColumns) => {
    // keep Bilty No fixed at index 0
    const fixed = columns.find((c) => c.accessor === "bilty_no");
    const others = newColumns.filter((c) => c.accessor !== "bilty_no");
    setColumns([fixed, ...others]);
  };

  const fetchConsignorSuggestions = async ({ value }) => {
    if (!value || value.length < 2) return setConsignorSuggestions([]);
    const payload = {
      name: value,
      search: "like",
      limit: "10",
      companyId: 1,
      additional_info: {
        branch_id: sessionObject.sessionVariables.branch_id,
        bilty_type: "4",
      },
    };
    try {
      const resp = await apiFetch(
        `${SERVER_URL}/consignor/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
        sessionObject
      );
      const json = await resp.json();
      setConsignorSuggestions(json || []);
    } catch (err) {
      console.error("Suggestion fetch error:", err);
      setConsignorSuggestions([]);
    }
  };

  const fetchMisReport = async (
    consignorId = null,
    pageNumber = 1,
    dateFrom = dateState.date_from,
    dateTo = dateState.date_to,
    customFilters = {},
    sortFields = []
  ) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const fYearObj = JSON.parse(
      sessionStorage.getItem("financial_year") || "{}"
    );
    const fyear = fYearObj.financial_year || "";
    const filterFields = {
      created_from: String(branchId),
      companyId: sessionObject.sessionVariables.company_id,
      fyear,
      ...customFilters,
    };
    if (consignorId) filterFields.consignor_id = consignorId;

    const payload = {
      paginate: { number_of_rows: 100, page_number: pageNumber },
      sort_fields: sortFields,
      filter_fields: filterFields,
      date_dict: { date_from: dateFrom, date_to: dateTo },
    };

    setFinalInput(payload);
    setLoading(true);
    setData([]);
    setTotalAmount(0);
    setPageCount(0);

    try {
      const resp = await apiFetch(
        `${SERVER_URL}/report/mis_report`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          signal: controller.signal,
        },
        sessionObject
      );
      const json = await resp.json();
      if (json.data && json.data.length > 0) {
        setData(json.data);
        const totalAmt = json.data.reduce(
          (sum, row) => sum + (row.total_amount || 0),
          0
        );
        setTotalAmount(totalAmt);
        setPageCount(Math.ceil((json.total_rows || json.data.length) / 100));
      } else {
        setData([]);
      }
    } catch (err) {
      if (err.name === "AbortError") {
        console.log("Request was cancelled");
        return;
      }
      console.error("MIS report fetch error:", err);
      setData([]);
    } finally {
      if (abortControllerRef.current === controller) {
        setLoading(false);
        abortControllerRef.current = null;
      }
    }
  };

  const onSuggestionSelected = async (event, { suggestion }) => {
    setConsignorInput(suggestion.consignor_name);
    setSelectedConsignorId(suggestion.consignor_id);

    if (suggestion.mis_columns?.mis_columns) {
      try {
        const parsed = JSON.parse(suggestion.mis_columns.mis_columns);
        setSavedMisColumns(parsed);
        const newFilters = {};
        parsed.forEach((a) => {
          newFilters[a] = true;
        });
        setFilters(newFilters);
      } catch {
        setSavedMisColumns([]);
        setFilters({});
      }
    } else {
      setSavedMisColumns([]);
      setFilters({});
    }

    try {
      const resp = await apiFetch(
        `${SERVER_URL}/transporter/${suggestion.consignor_id}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
        sessionObject
      );
      const transporterData = await resp.json();

      if (transporterData?.mis_mail) {
        const misMails = transporterData.mis_mail
          .split(",")
          .map((email) => email.trim())
          .filter((email) => email && email.includes("@"));

        if (misMails.length > 0) {
          const [firstEmail, ...restEmails] = misMails;
          setEmailForm((prev) => ({
            ...prev,
            email: firstEmail,
            cc_emails: restEmails.join(","),
          }));
        }
      }
    } catch (err) {
      console.error("Error fetching transporter data:", err);
    }

    fetchMisReport(
      suggestion.consignor_id,
      1,
      dateState.date_from,
      dateState.date_to
    );
  };

  const getSuggestionValue = (s) => s.consignor_name;
  const renderSuggestion = (s) => (
    <div>
      {s.consignor_name} - {s.consignor_gst}
    </div>
  );

  const handleTableFetch = useCallback(
    ({ pageIndex, pageSize, sortBy, customFilters, dateObject }) => {
      fetchMisReport(
        selectedConsignorId || null,
        pageIndex + 1,
        dateObject?.date_from || dateState.date_from,
        dateObject?.date_to || dateState.date_to,
        customFilters,
        sortBy || []
      );
    },
    [selectedConsignorId, dateState]
  );

  useEffect(() => {
    fetchMisReport();
  }, []);

  /** ------------ Filter popup ------------ */

  const applyFilter = async () => {
    const checkedAccessors = columns
      .filter((c) => filters[c.accessor])
      .map((c) => c.accessor);

    setSavedMisColumns(checkedAccessors);

    const payload = { mis_columns: JSON.stringify(checkedAccessors) };

    try {
      await apiFetch(
        `${SERVER_URL}/transporter/add_mis_columns?consignor_id=${selectedConsignorId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
        sessionObject
      );
    } catch (err) {
      console.error("Filter API error:", err);
    }
    setFilterOpen(false);
  };

  const toggleSelectAll = () => {
    const allSelected = columns
      .filter((c) => c.canFilter)
      .every((c) => filters[c.accessor]);
    if (allSelected) {
      setFilters({});
    } else {
      const newFilters = {};
      columns
        .filter((c) => c.canFilter)
        .forEach((c) => (newFilters[c.accessor] = true));
      setFilters(newFilters);
    }
  };

  const allSelected = columns
    .filter((c) => c.canFilter)
    .every((c) => filters[c.accessor]);

  async function handleSendMail(
    sessionObject,
    emailForm,
    visibleColumns,
    data,
    dateState,
    consignorInput
  ) {
    const keys = visibleColumns.map((c) => c.accessor);
    const headers = visibleColumns.map((c) => c.Header);

    const safeStringValue = (value, key) => {
      if (value === null || value === undefined) return "";

      if (key === "delivery_status") {
        if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
          const d = new Date(value);
          if (!isNaN(d)) {
            return d.toLocaleDateString("en-GB");
          }
        }
        return value;
      }

      if (typeof value === "string") return value;
      if (typeof value === "number") return value.toString();
      if (typeof value === "boolean") return value.toString();
      if (value instanceof Date) return value.toLocaleDateString("en-GB");

      return String(value);
    };

    const worksheetData = [];

    worksheetData.push([TRANSPORTER_NAME]);

    const fromDate = dateState?.date_from
      ? new Date(dateState.date_from).toLocaleDateString("en-GB")
      : "";
    const toDate = dateState?.date_to
      ? new Date(dateState.date_to).toLocaleDateString("en-GB")
      : "";
    worksheetData.push([`From Date:- ${fromDate} | To Date:- ${toDate}`]);

    worksheetData.push(["MIS Report"]);

    if (consignorInput && consignorInput.trim()) {
      worksheetData.push([`Party Name:- ${consignorInput.trim()}`]);
    }

    worksheetData.push([]);

    worksheetData.push(headers);

    data.forEach((row) => {
      const dataRow = visibleColumns.map((col) => {
        const val = col.renderExcel ? col.renderExcel(row) : row[col.accessor];
        return safeStringValue(val, col.accessor);
      });
      worksheetData.push(dataRow);
    });

    const ws = XLSX.utils.aoa_to_sheet(worksheetData);

    const numCols = headers.length;
    ws["!cols"] = Array(numCols).fill({ width: 20 });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "MIS_Report");

    const wbout = XLSX.write(wb, { bookType: "xls", type: "array" });

    const formData = new FormData();
    formData.append(
      "attachments",
      new Blob([wbout], {
        type: "application/vnd.ms-excel",
      }),
      "MIS_Report.xls"
    );

    const query = new URLSearchParams({
      subject: emailForm.subject || "Report",
      body: `${(emailForm.body_start || "").trim()}\n${(
        emailForm.body_end || ""
      ).trim()}`,
      receiver_email: emailForm.email,
      cc_emails: emailForm.cc_emails || "",
      user_id: sessionObject.sessionVariables.user_id,
    });

    const url = `${SERVER_URL}/mail/send_mail_for_yahoo_user_vise?${query}`;

    try {
      const resp = await fetch(url, {
        method: "POST",
        body: formData,
        headers: { accept: "application/json" },
      });

      const json = await resp.json();

      if (!resp.ok || json.flag === 0) {
        throw new Error(json.message || `HTTP ${resp.status}`);
      }

      return json;
    } catch (err) {
      throw err;
    }
  }

  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const resp = await apiFetch(
        `${SERVER_URL}/report/mis_report`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...finalInput,
            paginate: {
              number_of_rows: 999999,
              page_number: 1,
            },
          }),
        },
        sessionObject
      );

      const json = await resp.json();

      if (json.data) {
        const formattedData = json.data.map((row) => ({
          ...row,
          delivery_status: formatDateIfValid(row.delivery_status),
        }));

        setExcelData(formattedData);

        setTimeout(() => {
          download_ref.current?.handleDownload();
        }, 50);
      }
    } catch (err) {
      console.error("Excel download fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const isAnyModalOpen = filterOpen || sendEmailOpen;
    document.body.style.overflow = isAnyModalOpen ? "hidden" : "auto";
    return () => (document.body.style.overflow = "auto");
  }, [filterOpen, sendEmailOpen]);

  return (
    <div className="page-marfatiya-wise">
      <div className="report-bilty" style={{ position: "relative" }}>
        <div className="form-header">
          MIS Report
          {(variablesFromSession.role_id === "1" ||
            variablesFromSession.role_id === "2") && (
            <button
              disabled={!selectedConsignorId}
              style={{
                position: "absolute",
                right: "40px",
                top: "25px",
                background: selectedConsignorId ? "#23293e" : "#a0a0a0",
                color: "#fff",
                border: "none",
                padding: "8px 12px",
                borderRadius: "4px",
                cursor: selectedConsignorId ? "pointer" : "not-allowed",
                opacity: selectedConsignorId ? 1 : 0.6,
              }}
              onClick={() => selectedConsignorId && setFilterOpen(true)}
            >
              Filter
            </button>
          )}
        </div>

        <div className="report-bilty-table-container">
          {/* Date pickers & party input */}
          <div className="report-field-row">
            <div>
              From Date:
              <DatePicker
                dateFormat="dd-MM-yyyy"
                selected={dateState.date_from}
                onChange={(date) => {
                  setDateState({ ...dateState, date_from: date });
                  fetchMisReport(
                    selectedConsignorId || null,
                    1,
                    date,
                    dateState.date_to
                  );
                }}
              />
            </div>
            <div>
              To Date:
              <DatePicker
                dateFormat="dd-MM-yyyy"
                selected={dateState.date_to}
                onChange={(date) => {
                  setDateState({ ...dateState, date_to: date });
                  fetchMisReport(
                    selectedConsignorId || null,
                    1,
                    dateState.date_from,
                    date
                  );
                }}
              />
            </div>
          </div>

          <div className="chform-row">
            <label className="form-label">Party Name</label>
            <Autosuggest
              suggestions={consignorSuggestions}
              onSuggestionsFetchRequested={fetchConsignorSuggestions}
              onSuggestionsClearRequested={() => setConsignorSuggestions([])}
              getSuggestionValue={getSuggestionValue}
              renderSuggestion={renderSuggestion}
              inputProps={{
                value: consignorInput,
                onChange: (e, { newValue }) => setConsignorInput(newValue),
              }}
              onSuggestionSelected={onSuggestionSelected}
            />
          </div>
          {!selectedConsignorId && savedMisColumns.length === 0 && (
            <div
              style={{
                marginBottom: "6px",
                color: "green",
                fontSize: "1rem",
                fontWeight: "500",
              }}
            >
              All report columns are currently visible. To personalize your
              view, search and select a party above.
            </div>
          )}

          {selectedConsignorId && savedMisColumns.length === 0 && (
            <div
              style={{
                marginBottom: "6px",
                color: "red",
                fontSize: "1rem",
                fontWeight: "500",
              }}
            >
              No saved columns for this party. Use <b>Filter</b> to select
              columns and click <b>Apply</b> to save them.
            </div>
          )}

          {/* Draggable Report Table */}
          <DraggableReportTable
            columns={visibleColumns}
            data={data}
            fetchData={handleTableFetch}
            dateObject={dateState}
            loading={loading}
            pageCount={pageCount}
            onColumnReorder={handleColumnReorder}
            title="MIS Report"
          />

          {/* Excel download */}
          <div className="form-footer">
            <button style={{ display: "none" }}>
              <ReactHTMLTableToExcel
                id="test-table-xls-button"
                className="download-table-xls-button"
                table="mis_report_excel"
                filename="tablexls"
                sheet="tablexls"
                buttonText="Download as XLS"
                ref={(a) => (download_ref.current = a)}
              />
            </button>

            <button
              className="download-table-xls-button"
              onClick={handleDownloadExcel}
            >
              Download as XLS
            </button>
            {ReportExcel.mis_report_excel({
              columns: visibleColumns,
              ex_data: excelData,
              dateState,
            })}

            <button
              className="btn btn-primary"
              onClick={async () => {
                try {
                  const { user_id, company_id } =
                    sessionObject.sessionVariables || {};
                  const finalCompanyId = company_id ?? 1;

                  const resp = await fetch(
                    `${SERVER_URL}/mail/for_mis_mail/${finalCompanyId}?user_id=${user_id}`,
                    {
                      method: "GET",
                      headers: { Accept: "application/json" },
                    }
                  );

                  if (!resp.ok) {
                    throw new Error(`HTTP error ${resp.status}`);
                  }

                  const mailData = await resp.json();

                  const newEmailForm = {
                    email_from: mailData.sender || "",
                    email: emailForm.email || mailData.to || "",
                    cc_emails: emailForm.cc_emails || mailData.cc || "",
                    subject: mailData.subject || "",
                    body_start: mailData.body_start || "",
                    body_end: mailData.body_end || "",
                  };

                  setEmailForm(newEmailForm);
                  setSendEmailOpen(true);
                } catch (error) {
                  console.error("Error fetching mail data:", error);

                  setEmailForm((prev) => ({
                    email_from:
                      sessionObject.sessionVariables?.user_email || "",
                    email: prev.email || "",
                    cc_emails: prev.cc_emails || "",
                    subject: "",
                    body_start: "",
                    body_end: "",
                  }));
                  setSendEmailOpen(true);
                }
              }}
              disabled={!selectedConsignorId || data.length === 0}
              style={{
                opacity: !selectedConsignorId || data.length === 0 ? 0.6 : 1,
              }}
            >
              Send Mail
            </button>

            <Popup
              open={sendEmailOpen}
              modal
              closeOnDocumentClick={false}
              onClose={() => setSendEmailOpen(false)}
              contentStyle={{
                width: "600px",
                maxWidth: "100%",
                borderRadius: "8px",
              }}
            >
              {(close) => (
                <div className="pop-up-container">
                  <div className="pop-up-header">
                    Send Email
                    <div>
                      <a
                        className="pop-up-close btn"
                        onClick={() => {
                          setEmailStatus({
                            show: false,
                            type: "",
                            message: "",
                          });
                          setEmailForm({
                            email_from: "",
                            email: "",
                            cc_emails: "",
                            subject: "",
                            body_start: "",
                            body_end: "",
                          });
                          close();
                        }}
                      >
                        &times;
                      </a>
                    </div>
                  </div>

                  <div className="popup-content">
                    <div className="pop-up-fields">
                      <div className="pop-up-field">
                        <label>From</label>
                        <input
                          className="form-input"
                          value={emailForm.email_from || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            setEmailForm((prevForm) => ({
                              ...prevForm,
                              email_from: value || "",
                            }));
                          }}
                        />
                      </div>
                      <div className="pop-up-field">
                        <label>To</label>
                        <input
                          className="form-input"
                          type="email"
                          value={emailForm.email || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            setEmailForm((prevForm) => ({
                              ...prevForm,
                              email: value || "",
                            }));
                          }}
                        />
                      </div>
                      <div className="pop-up-field">
                        <label>CC</label>
                        <textarea
                          className="form-input"
                          style={{ height: "70px" }}
                          value={emailForm.cc_emails || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            setEmailForm((prevForm) => ({
                              ...prevForm,
                              cc_emails: value || "",
                            }));
                          }}
                        />
                      </div>
                      <div className="pop-up-field">
                        <label>Subject</label>
                        <textarea
                          className="form-input"
                          rows={3}
                          style={{ height: "70px" }}
                          value={emailForm.subject || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            setEmailForm((prevForm) => ({
                              ...prevForm,
                              subject: value || "",
                            }));
                          }}
                        />
                      </div>
                      <div className="pop-up-field">
                        <label>Body Start</label>
                        <textarea
                          className="form-input"
                          rows={3}
                          style={{ height: "70px" }}
                          value={emailForm.body_start || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            setEmailForm((prevForm) => ({
                              ...prevForm,
                              body_start: value || "",
                            }));
                          }}
                        />
                      </div>
                      <div className="pop-up-field">
                        <label>Body End</label>
                        <textarea
                          className="form-input"
                          rows={3}
                          style={{ height: "70px" }}
                          value={emailForm.body_end || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            setEmailForm((prevForm) => ({
                              ...prevForm,
                              body_end: value || "",
                            }));
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pop-up-actions">
                    {emailStatus.show ? (
                      <div style={{ textAlign: "center", padding: "20px" }}>
                        <div
                          style={{
                            padding: "15px",
                            borderRadius: "5px",
                            marginBottom: "15px",
                            backgroundColor:
                              emailStatus.type === "success"
                                ? "#d4edda"
                                : "#f8d7da",
                            color:
                              emailStatus.type === "success"
                                ? "#155724"
                                : "#721c24",
                            border: `1px solid ${
                              emailStatus.type === "success"
                                ? "#c3e6cb"
                                : "#f5c6cb"
                            }`,
                          }}
                        >
                          <strong>
                            {emailStatus.type === "success"
                              ? "Success!"
                              : "Error!"}
                          </strong>
                          <br />
                          {emailStatus.message}
                        </div>
                        <button
                          className="pop-up-button"
                          onClick={() => {
                            setEmailStatus({
                              show: false,
                              type: "",
                              message: "",
                            });
                            setEmailForm({
                              email_from: "",
                              email: "",
                              cc_emails: "",
                              subject: "",
                              body_start: "",
                              body_end: "",
                            });
                            close();
                          }}
                          style={{
                            backgroundColor: "#23293e",
                            color: "white",
                            padding: "10px 20px",
                            border: "none",
                            borderRadius: "5px",
                            cursor: "pointer",
                          }}
                        >
                          OK
                        </button>
                      </div>
                    ) : (
                      <button
                        className="pop-up-button"
                        onClick={async () => {
                          setLoading(true);
                          try {
                            const resp = await apiFetch(
                              `${SERVER_URL}/report/mis_report`,
                              {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  ...finalInput,
                                  paginate: {
                                    number_of_rows: 999999,
                                    page_number: 1,
                                  },
                                }),
                              },
                              sessionObject
                            );

                            const json = await resp.json();
                            const allRows = json?.data || [];

                            await handleSendMail(
                              sessionObject,
                              emailForm,
                              visibleColumns,
                              allRows,
                              dateState,
                              consignorInput
                            );
                            setEmailStatus({
                              show: true,
                              type: "success",
                              message:
                                "Email with Excel attachment sent successfully!",
                            });
                            setEmailForm({
                              email_from: "",
                              email: "",
                              cc_emails: "",
                              subject: "",
                              body_start: "",
                              body_end: "",
                            });
                          } catch (err) {
                            console.error("Email send error:", err);
                            setEmailStatus({
                              show: true,
                              type: "error",
                              message:
                                err.message ||
                                "Failed to send email with Excel.",
                            });
                          } finally {
                            setLoading(false);
                          }
                        }}
                        disabled={
                          loading || !emailForm.email || !emailForm.subject
                        }
                        style={{
                          backgroundColor:
                            loading || !emailForm.email || !emailForm.subject
                              ? "#583f3f"
                              : "#23293e",
                          color: "white",
                          padding: "10px 20px",
                          border: "none",
                          borderRadius: "5px",
                          cursor: "pointer",
                          opacity:
                            loading || !emailForm.email || !emailForm.subject
                              ? 0.6
                              : 1,
                        }}
                      >
                        {loading ? "Sending..." : "Send"}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </Popup>
          </div>
        </div>
      </div>

      {/* Filter popup */}
      {filterOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 999,
          }}
          onClick={() => setFilterOpen(false)}
        >
          <div
            style={{
              background: "#fff",
              padding: "20px",
              borderRadius: "8px",
              minWidth: "250px",
              maxHeight: "70vh",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Select Columns</h3>

            <div
              style={{
                overflowY: "auto",
                flex: 1,
                marginBottom: "10px",
              }}
            >
              {columns
                .filter((c) => c.canFilter)
                .map((c) => (
                  <label
                    key={c.accessor}
                    style={{ display: "block", marginBottom: "6px" }}
                  >
                    <input
                      type="checkbox"
                      checked={filters[c.accessor] || false}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          [c.accessor]: e.target.checked,
                        })
                      }
                    />{" "}
                    {c.Header}
                  </label>
                ))}
            </div>

            <div style={{ textAlign: "right" }}>
              <button
                onClick={toggleSelectAll}
                style={{
                  padding: "5px 10px",
                  cursor: "pointer",
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  marginRight: "8px",
                }}
              >
                {allSelected ? "Deselect All" : "Select All"}
              </button>
              <button
                onClick={() => setFilterOpen(false)}
                style={{
                  marginRight: "8px",
                  padding: "4px 10px",
                  cursor: "pointer",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  background: "transparent",
                }}
              >
                Cancel
              </button>
              <button
                onClick={applyFilter}
                style={{
                  padding: "5px 10px",
                  cursor: "pointer",
                  backgroundColor: "#4caf50",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MISReport;
